import { Component, computed, inject, Input, OnDestroy, OnInit, signal } from '@angular/core';
import { ModalController, IonicModule } from '@ionic/angular';
import { AppMasterDBFacade, InputAppClientDBFacade } from '@monorepo/core-data';
import { BarcodeScannerService, ScannerDataFormatter } from '@monorepo/services';
import {
  BctxViz,
  TBctxItemWithoutSize,
  TBCTXKeys,
  TProductionReasonDTO,
  TSessionBlockWithReasons,
  TTagType,
} from '@monorepo/types';
import { Subscription } from 'rxjs';
import { PRODUCTION_HOUR_TO_REASON_MAP_TABLE_MODEL } from './production-hour-to-reason-map-table-model';
import { ProductionReasonListComponent } from '../production-reason-list/production-reason-list.component';
import { TRowActionEvent, UiModule } from '@monorepo/ui';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

import { ProductionReasonLoggingService } from '../../services/production-reason-logging';
import { InputAppAPIHelperUtil } from '@monorepo/utils';
@Component({
  selector: 'monorepo-production-hour-to-reason-map',
  templateUrl: './production-hour-to-reason-map.component.html',
  styleUrls: ['./production-hour-to-reason-map.component.scss'],
  standalone: true,
  imports: [CommonModule, IonicModule, UiModule, FormsModule],
})
export class ProductionHourToReasonMapComponent implements OnInit, OnDestroy {
  @Input() isPerPieceApp: boolean = false;
  private modalController = inject(ModalController);
  private inputAppClientDBFacade = inject(InputAppClientDBFacade);
  private barcodeScannerService = inject(BarcodeScannerService);
  private appMasterDBFacade = inject(AppMasterDBFacade);
  private productionReasonLoggingService = inject(ProductionReasonLoggingService);
  private scannerDataFormatter = inject(ScannerDataFormatter);
  private inputAppAPIHelperUtil = inject(InputAppAPIHelperUtil);

  displayTag = '';
  subscriptions: Subscription[] = [];
  productionReasonList: TProductionReasonDTO[] = [];
  bctxViz = signal<BctxViz[]>([]);
  sessionBlocks = signal<TSessionBlockWithReasons[]>([]);
  selectedBctxIno = signal<TBctxItemWithoutSize[]>([]);
  headerInput = computed(() => {
    const bctxKeyToValueMap = this.selectedBctxIno().reduce((acc, curr) => {
      acc[curr.k] = curr.v;
      return acc;
    }, {} as Record<TBCTXKeys, string>);
    return this.bctxViz().map((bctx) => ({
      label: bctx.d,
      value: bctxKeyToValueMap[bctx.k] || '--',
    }));
  });
  tableModel = computed(() => PRODUCTION_HOUR_TO_REASON_MAP_TABLE_MODEL.createTable(this.sessionBlocks()));

  ngOnInit(): void {
    this.init();
  }

  init() {
    const s1 = this.appMasterDBFacade.getBctxViz().subscribe((bctxViz) => {
      this.bctxViz.set(
        bctxViz.filter((item) => item.k !== 'size' && item.k !== 'display_piece_tag' && item.k !== 'search_id'),
      );
    });

    const s2 = this.inputAppClientDBFacade.getselectedBctxItem().subscribe((res) => {
      this.selectedBctxIno.set(res.bctxInfoWithoutSize.list);
      this.sync();
    });

    this.subscriptions.push(s1, s2);
  }

  loadSessionPlans() {
    const bctx = this.getBctxFromSelectedBctxInfo();
    if (bctx.length === 0) {
      return;
    }

    this.productionReasonLoggingService.loadSessionPlans(bctx).subscribe({
      next: (res) => {
        if (res && res.session_plan_list && res.session_plan_list.length > 0) {
          this.sessionBlocks.set(res.session_plan_list);
        }
      },
      error: () => {},
    });
  }

  loadProductionReasonList() {
    this.productionReasonLoggingService.getProductionReasonList().subscribe((res) => {
      this.productionReasonList = res.productionReasons;
    });
  }

  async openReasonsModal(rowItem: TSessionBlockWithReasons) {
    const modal = await this.modalController.create({
      component: ProductionReasonListComponent,
      backdropDismiss: false,
      cssClass: 'roaming-qc-select-operation-modal',
      componentProps: {
        productionReasonList: this.productionReasonLoggingService.transformProductionReasonList(
          this.productionReasonList,
          rowItem.reasons,
        ),
      },
    });
    modal.present();

    const modalData = await modal.onDidDismiss();
    if (modalData.data as string[]) {
      this.save(modalData.data, rowItem.session_block, rowItem.versioned_id);
    }
  }

  save(reasons: string[], session_block: string, versioned_id?: string | null) {
    const bctx = this.getBctxFromSelectedBctxInfo();
    if (bctx.length === 0) {
      return;
    }
    this.productionReasonLoggingService.save(bctx, reasons, session_block, versioned_id).subscribe((res) => {
      if (res && res.status === 'SUCCESS') {
        this.loadSessionPlans();
      }
    });
  }

  getBctxFromSelectedBctxInfo() {
    const bctxKeyToValueMap = this.selectedBctxIno().reduce((acc, curr) => {
      acc[curr.k] = curr.v;
      return acc;
    }, {} as Record<TBCTXKeys, string>);
    const bctx: TBctxItemWithoutSize[] = this.bctxViz().map((item) => ({
      k: item.k,
      v: bctxKeyToValueMap[item.k] || '',
    })) as TBctxItemWithoutSize[];
    return bctx;
  }

  getTagDetails(displayTag: string) {
    if (this.inputAppAPIHelperUtil.getTagTypeFromTagId(displayTag) !== TTagType.PER_PIECE_TAG) {
      this.inputAppAPIHelperUtil.showPopup('Error', 'Please scan a per piece tag');
      return;
    }
    this.inputAppClientDBFacade.getPerPieceTagDetails(displayTag).subscribe((res) => {
      if (res.length) {
        const newSelectedBctxIno = res.filter((item) => item.k !== 'size') as TBctxItemWithoutSize[];
        this.selectedBctxIno.set(newSelectedBctxIno);
        this.loadSessionPlans();
      } else {
        this.inputAppAPIHelperUtil.showPopup('Error', 'No tag details found');
      }
    });
  }

  async scanTag() {
    const scanResponse = await this.barcodeScannerService.openBarcodeScanner<string>(true);
    switch (scanResponse.type) {
      case 'SUCCESS_STRING': {
        this.handleScannedTag(scanResponse.scanText);
        break;
      }
      default:
        break;
    }
  }

  private async handleScannedTag(scanText: string): Promise<void> {
    console.log('[SCAN TEXT]', scanText);
    const formattedTagData = this.scannerDataFormatter.decodeTagString(scanText);
    console.log('[FORMATTED TAG DATA]', formattedTagData);
    if (formattedTagData.status && formattedTagData.data['did']) {
      await this.getTagDetails(formattedTagData.data['did'] as string);
    } else {
      this.inputAppAPIHelperUtil.showPopup('Error', 'Something went wrong while scanning the tag');
    }
  }

  onRowAction(event: TRowActionEvent<TSessionBlockWithReasons>) {
    if (event.actionType === 'EDIT') {
      this.openReasonsModal(event.rowItem);
    }
  }

  clear() {
    this.selectedBctxIno.set([]);
    this.displayTag = '';
  }
  close() {
    this.modalController.dismiss();
  }

  sync() {
    this.loadSessionPlans();
    this.loadProductionReasonList();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }
}
